import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import { fetchTeChronoRecords } from "../../api/coachChronoApi";
import { formatChronoClock } from "../../utils/coachChronoPlayers";
import { ReviewVideoFullscreenPlayer } from "../../components/ReviewVideoFullscreenPlayer";
import {
  peekSignedMediaUrl,
  resolveSignedMediaUrl,
} from "../../api/signedMediaUrl";
import "./LiveTrainingCoachChronoPanel.css";

/**
 * Player (or coach-as-player) entry to review their chrono performance segment
 * for the current off-table exercise.
 */
export function LiveTrainingPlayerChronoReview({
  teId,
  userId,
  brandPrimary = "#0877BC",
  brandSecondary = "#46528b",
}) {
  const { t } = useTranslation();
  const [record, setRecord] = useState(null);
  const [open, setOpen] = useState(false);
  const [playUrl, setPlayUrl] = useState(null);

  useEffect(() => {
    if (!teId || !userId) {
      setRecord(null);
      return undefined;
    }
    const ac = new AbortController();
    fetchTeChronoRecords(teId, { signal: ac.signal })
      .then((data) => {
        const mine = (data?.records || []).find(
          (r) => String(r.user_id) === String(userId)
        );
        if (mine?.video?.url && (mine.video.start_ms != null || mine.video.end_ms != null)) {
          setRecord(mine);
        } else {
          setRecord(null);
        }
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setRecord(null);
      });
    return () => ac.abort();
  }, [teId, userId]);

  useEffect(() => {
    if (!open || !record?.video?.url) {
      setPlayUrl(null);
      return undefined;
    }
    let cancelled = false;
    const raw = record.video.url;
    const cached = peekSignedMediaUrl(raw);
    setPlayUrl(cached || raw);
    if (cached) return undefined;
    resolveSignedMediaUrl(raw)
      .then((signed) => {
        if (!cancelled && signed) setPlayUrl(signed);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, record]);

  const handleOpen = useCallback(() => setOpen(true), []);

  if (!record?.video?.url) return null;

  const startSec =
    record.video.start_ms != null
      ? Math.max(0, Number(record.video.start_ms) / 1000)
      : 0;
  const endSec =
    record.video.end_ms != null
      ? Math.max(startSec, Number(record.video.end_ms) / 1000)
      : null;
  const timeLabel =
    record.metric_value != null
      ? formatChronoClock(Number(record.metric_value))
      : null;

  return (
    <>
      <button
        type="button"
        className="lt-chrono-panel__btn lt-chrono-panel__btn--review lt-player-chrono-review"
        onClick={handleOpen}
        style={{
          "--lt-brand": brandPrimary,
          "--lt-brand-secondary": brandSecondary,
        }}
      >
        <MovieOutlinedIcon fontSize="small" />
        {t("liveTrainingChronoMyReview") || "Review my chrono"}
        {timeLabel ? ` · ${timeLabel}` : ""}
      </button>
      {open && playUrl ? (
        <ReviewVideoFullscreenPlayer
          url={playUrl}
          onClose={() => setOpen(false)}
          title={t("liveTrainingChronoMyReview") || "Review my chrono"}
          autoPlay
          enablePose={false}
          enableJointOverlay
          isEvaluationVideo={false}
          messageId={record.video.message_id || null}
          teId={teId}
          initialCurrentTime={startSec}
          playbackWindowStart={startSec}
          playbackWindowEnd={endSec}
        />
      ) : null}
    </>
  );
}

export default LiveTrainingPlayerChronoReview;
